import React, { useState, useMemo } from 'react';

import { Select, MenuItem, FormControl, InputLabel, Typography, Box, Card, useTheme } from '@mui/material';

import CustomImageComponent from '@/components/shared/custom/custom-image';
import { LEAGUES } from '@/constants/leagues';
import { obtenerEquipo } from '@/data/equipos';
import { translate } from '@/data/fixture-transalations';
import { getMasterPhotoById, getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage, getCompetitionName } from '@/helpers/leagues-helper';

const estadisticas = [
    'Minutos',
    'Goles',
    'Asistencias',
    'Disparos a puerta',
    'Disparos entre los tres palos',
    'Tarjetas amarillas',
    'Tarjetas rojas',
    'Faltas cometidas',
    'Faltas recibidas',
    'Goles en propia puerta',
    'Paradas',
    'Penales fallados',
    'Penales anotados',
    'Penales parados',
];

const ResumenTab = ({ data, teams, season }) => {
    const [statType, setStatType] = useState('minutos');
    const [selectedMatch, setSelectedMatch] = useState('total');
    const [selectedLeague, setSelectedLeague] = useState(LEAGUES.LA_LIGA);

    const theme = useTheme();

    const handleStatTypeChange = (event) => {
        setStatType(event.target.value);
    };

    const handleMatchChange = (event) => {
        setSelectedMatch(event.target.value);
    };

    const handleLeagueChange = (event) => {
        setSelectedLeague(event.target.value);
    };

    const leagues = useMemo(() => {
        const leagueSet = new Set();
        data?.partidos?.forEach((partido) => {
            leagueSet.add(partido.leagueId);
        });

        return ['all', ...Array.from(leagueSet)];
    }, [data]);

    const getTotalStat = (playerId, statType, matchFilter, leagueFilter) => {
        let total = 0;

        data?.partidos.forEach((partido) => {
            if (
                (leagueFilter === 'all' || leagueFilter === partido.leagueId) &&
                (matchFilter === 'total' || matchFilter === partido.fixture.id)
            ) {
                partido.stats.forEach((stat) => {
                    stat.players.forEach((player) => {
                        if (player.player.id === playerId) {
                            switch (statType) {
                                case 'minutos':
                                    if (!isNaN(player.statistics[0]?.games?.minutes)) {
                                        total += Number(player.statistics[0]?.games?.minutes || 0);
                                    }
                                    break;
                                case 'goles':
                                    total += player.statistics[0]?.goals?.total || 0;
                                    break;
                                case 'asistencias':
                                    total += player.statistics[0]?.goals?.assists || 0;
                                    break;
                                case 'tarjetas amarillas':
                                    total += player.statistics[0]?.cards?.yellow || 0;
                                    break;
                                case 'tarjetas rojas':
                                    total += player.statistics[0]?.cards?.red || 0;
                                    break;
                                case 'faltas cometidas':
                                    total += player.statistics[0]?.fouls?.committed || 0;
                                    break;
                                case 'faltas recibidas':
                                    total += player.statistics[0]?.fouls?.drawn || 0;
                                    break;
                                case 'penales fallados':
                                    total += player.statistics[0]?.penalty?.missed || 0;
                                    break;
                                case 'penales anotados':
                                    total += player.statistics[0]?.penalty?.scored || 0;
                                    break;
                                case 'goles en propia puerta':
                                    total += player.statistics[0]?.goals?.owngoals || 0;
                                    break;
                                case 'paradas':
                                    total += player.statistics[0]?.goals?.conceded || 0;
                                    break;
                                case 'penales parados':
                                    total += player.statistics[0]?.penalty?.saved || 0;
                                    break;
                                case 'disparos a puerta':
                                    total += player.statistics[0]?.shots?.total || 0;
                                    break;
                                case 'disparos entre los tres palos':
                                    total += player.statistics[0]?.shots?.on || 0;
                                    break;
                                default:
                                    break;
                            }
                        }
                    });
                });
            }
        });

        return total;
    };

    const allPlayers = useMemo(() => {
        const players = [];
        data?.partidos?.forEach((partido) => {
            partido.stats.forEach((stat) => {
                stat.players.forEach((player) => {
                    if (!players.some((p) => p.player.id === player.player.id)) {
                        players.push(player);
                    }
                });
            });
        });
        return players;
    }, [data]);

    const sortedPlayers = allPlayers
        .map((player) => ({
            ...player,
            totalStat: getTotalStat(player.player.id, statType, selectedMatch, selectedLeague),
        }))
        .sort((a, b) => b.totalStat - a.totalStat);

    const getTeamImage = (team) => {
        const teamFound = obtenerEquipo(team.id);
        return teamFound ? getTeamImageById(teamFound.id) : team.logo;
    };

    const getMatchLabel = (partido) => {
        const homeTeam = partido?.home?.team;
        const awayTeam = partido?.away?.team;

        return (
            <Box display="flex" alignItems="center">
                <div style={{ width: '60px' }}>
                    <CustomImageComponent
                        src={getCompetitionImage(partido.leagueId)}
                        alt={getCompetitionName(partido.leagueId)}
                        width={20}
                        height={20}
                        style={{ marginRight: '4px' }}
                    />
                    <span>{translate(partido.round, true)}</span>
                </div>
                <CustomImageComponent
                    src={getTeamImage(homeTeam)}
                    alt={homeTeam.name}
                    width={20}
                    height={20}
                    style={{ marginRight: '4px' }}
                />
                {homeTeam.name} - {awayTeam.name}
                <CustomImageComponent
                    src={getTeamImage(awayTeam)}
                    alt={awayTeam.name}
                    width={20}
                    height={20}
                    style={{ marginLeft: '4px' }}
                />
            </Box>
        );
    };

    const matches = data?.partidos
        ?.filter(
            (partido) => teams.includes(partido?.home?.team?.id) || teams.includes(partido?.away?.team?.id)
        )
        ?.sort((a, b) => new Date(a.fixture.date) - new Date(b.fixture.date));

    const getCard = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    bgcolor: 'background.paper',
                }}
            >
                {sortedPlayers
                    .filter((player) => player.totalStat > 0)
                    .map((player) => (
                        <Card
                            key={player.player.id}
                            sx={{
                                width: { xs: '33.3333%', md: 80 },
                                height: 120,
                                p: 0.5,
                                bgcolor: 'background.default',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    bgcolor: 'background.card',
                                    border: `1px solid ${theme.palette.border.main}`,
                                }}
                            >
                                <CustomImageComponent
                                    src={getMasterPhotoById(player?.player?.id, 60)}
                                    width={60}
                                    height={60}
                                    alt={player?.player?.name}
                                    style={{
                                        marginBottom: 8, // Espacio debajo de la imagen
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        width: '100%',
                                        textAlign: 'center',
                                        borderRadius: '2px',
                                        margin: '0 6px',
                                        fontSize: '12px',
                                        marginBottom: '4px',
                                        backgroundColor: 'background.default',
                                    }}
                                >
                                    {player?.player?.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    {player.totalStat}
                                </Typography>
                            </Box>
                        </Card>
                    ))}
            </Box>
        );
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h3" gutterBottom className="mb-25">
                Resumen de estadísticas {season > 0 ? `de la temporada ${season}/${season + 1}` : ''}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 3,
                }}
            >
                <FormControl variant="standard" sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel id="league-select-label">Competición</InputLabel>
                    <Select
                        labelId="league-select-label"
                        id="league-select"
                        value={selectedLeague}
                        onChange={handleLeagueChange}
                    >
                        {leagues.map((leagueId) => (
                            <MenuItem key={leagueId} value={leagueId}>
                                {leagueId !== 'all' && (
                                    <CustomImageComponent
                                        src={getCompetitionImage(leagueId)}
                                        alt={getCompetitionName(leagueId)}
                                        width={20}
                                        height={20}
                                        style={{ marginRight: '4px' }}
                                    />
                                )}
                                {leagueId !== 'all' ? getCompetitionName(leagueId) : 'Todas'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="standard" sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel id="stat-type-label">Estadística</InputLabel>
                    <Select
                        labelId="stat-type-label"
                        id="stat-type"
                        value={statType}
                        onChange={handleStatTypeChange}
                    >
                        {estadisticas.map((estadistica) => (
                            <MenuItem key={estadistica} value={estadistica.toLowerCase()}>
                                {estadistica}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="standard" sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel id="match-select-label">Jornada</InputLabel>
                    <Select
                        labelId="match-select-label"
                        id="match-select"
                        value={selectedMatch}
                        onChange={handleMatchChange}
                    >
                        <MenuItem value="total">Total</MenuItem>
                        {matches
                            ?.filter((x) => selectedLeague === 'all' || x.leagueId === selectedLeague)
                            ?.sort((a, b) => b?.fixture?.id - a?.fixture?.id)
                            .map((partido) => (
                                <MenuItem key={partido.fixture.id} value={partido.fixture.id}>
                                    {getMatchLabel(partido)}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>
            {getCard()}
        </Box>
    );
};

export default ResumenTab;
